import { useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useBeforeMount } from '@24hr/react-hooks';
import { Image } from '../../image/image';
import { Button } from '../../typography/button/button';
import { useInsights } from '@/hooks/use-insights';
import {
    INSIGHTS_QUERY_KEY,
    INSIGHTS_SETTINGS_KEY,
    INSIGHT_CATEGORIES_QUERY_KEY,
    getInsightCategories,
    getInsightSettings,
    getInsights,
} from '@/queries/insights';
import { AppContext } from '@/contexts/app-context';
import { getImageUrl } from '@/utils/images';
import Pagination from '../../pagination/pagination';
import { Terms } from './components/terms';
import { InsightsMeta } from './components/insights-meta';
import { Filters } from './components/filters';

export const Insights = () => {
    const context = useContext(AppContext);
    const { basePath, termMessage, sortedTerms, currentFilters, goTo } = useInsights();

    useBeforeMount(() => {
        context.prefetch({
            queryKey: [INSIGHT_CATEGORIES_QUERY_KEY],
            queryFn: () => getInsightCategories(context),
        });
        context.prefetch({ queryKey: [INSIGHTS_QUERY_KEY], queryFn: () => getInsights(context) });
        context.prefetch({ queryKey: [INSIGHTS_SETTINGS_KEY], queryFn: () => getInsightSettings(context) });
    });

    const header = useMemo(() => currentFilters?.term?.title || 'Resurs Insights', [currentFilters]);

    function paginate(page: number) {
        goTo('page', `${page}`);
        scrollTo(0, 0);
    }

    return (
        <div data-component="insights">
            <InsightsMeta currentTerm={currentFilters.term} page={currentFilters.page} group={currentFilters.group} />

            <div>
                <h1 className="r-pb-6 r-text-3xl r-font-bold md:r-pb-8 md:r-text-4xl">{header}</h1>
                <Filters currentFilters={currentFilters} basePath={basePath} sortedTerms={sortedTerms} goTo={goTo} />

                <div className="r-grid r-gap-y-16 r-pt-4 md:r-grid-cols-2 md:r-gap-x-8 lg:r-grid-cols-3">
                    {currentFilters.insights.map(({ image, title, preamble, terms = [], slug }, index) => (
                        <div key={index} className="max-md:r-col-span-2" style={{ order: index + 1 }}>
                            <Link to={`${basePath}/${slug}`}>
                                {image ? (
                                    <Image
                                        src={image.src}
                                        size="500xAUTO"
                                        altText={image.alt_text}
                                        width={image.originalSize.width}
                                        height={image.originalSize.height}
                                    />
                                ) : null}
                                <h2 className="r-mb-4 r-text-2xl r-font-bold">{title}</h2>
                                <div className="r-mb-4" dangerouslySetInnerHTML={{ __html: preamble }} />
                            </Link>
                            <Terms terms={terms} sortedTerms={sortedTerms} basePath={basePath} goTo={goTo} group={currentFilters.group} />
                        </div>
                    ))}
                    {termMessage ? (
                        <div
                            className="r-order-3 r-col-span-2 r-rounded-2xl r-bg-cover r-bg-center r-bg-no-repeat r-p-4 r-shadow-lg md:r-min-h-96 md:r-p-12 md:max-lg:r-order-4 lg:r-p-16"
                            style={{
                                backgroundImage: `url(${getImageUrl(termMessage.image.src, '500xAUTO')})`,
                            }}
                        >
                            <h2 className="r-mb-16 r-max-w-md r-text-3xl r-font-bold r-text-white md:r-text-4xl">{termMessage.message}</h2>
                            <Button to={termMessage.link.url} target={termMessage.link.target || '_self'}>
                                {termMessage.link.title}
                            </Button>
                        </div>
                    ) : null}
                </div>

                <div className="r-mt-8 r-border-t r-border-gray-200 r-py-8">
                    <Pagination
                        paginate={paginate}
                        group={currentFilters.group}
                        count={currentFilters.count}
                        perPage={currentFilters.perPage}
                        currentPage={currentFilters.page}
                    />
                </div>
            </div>
        </div>
    );
};
